import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Descriptions, Table, Tag, Typography } from 'antd';
import { getMemberCommunicationProcessing } from '@/services/technical';
import CommunicationWindowRetryButton from './CommunicationWindowRetryButton';
import styles from './SimpleWorkspace.less';

const WINDOW_MILLIS = 24 * 60 * 60 * 1000;
const isTime = (value) =>
  Number.isSafeInteger(value) && value > 0 && value <= 8640000000000000;
export function readCommunicationProcessing(response, memberId) {
  const data = response?.data;
  if (
    !['00000', '0'].includes(String(response?.code)) ||
    String(data?.memberId) !== String(memberId) ||
    !isTime(data?.fromTime) ||
    !isTime(data?.toTime) ||
    !isTime(data?.queriedAt) ||
    data.toTime - data.fromTime !== WINDOW_MILLIS ||
    data.queriedAt < data.toTime
  ) {
    throw new Error('Invalid status scope');
  }
  const counts = [
    'totalTaskCount',
    'pendingCount',
    'runningCount',
    'succeededCount',
    'failedCount',
  ].map((key) => data[key]);
  if (
    counts.some(
      (value) => !Number.isInteger(value) || value < 0 || value > 2147483647,
    ) ||
    counts.slice(1).reduce((sum, value) => sum + value, 0) !== counts[0] ||
    [data.nextReadyAt, data.latestFinishedAt].some(
      (value) => value != null && !isTime(value),
    )
  ) {
    throw new Error('Invalid status counts or timestamps');
  }
  validateWindowTasks(data);
  return data;
}
const SOURCES = {
  workwx_chat: '企微群聊',
  guided_course: '指导课沟通',
  live_coaching: '1V1 沟通',
  unknown: '未知来源',
};
const TASK_STATES = {
  pending: '等待清洗',
  running: '清洗中',
  succeeded: '清洗成功',
  failed: '清洗失败',
  unknown: '未知状态',
};
function validateWindowTasks(data) {
  const tasks = data.windowTasks;
  const more = data.windowTasksHasMore;
  if (tasks == null && more == null) return;
  if (
    !Array.isArray(tasks) ||
    typeof more !== 'boolean' ||
    tasks.length !== Math.min(20, data.totalTaskCount) ||
    more !== data.totalTaskCount > 20
  )
    throw new Error('Invalid cleaning task list');
  const remaining = {
    pending: data.pendingCount,
    running: data.runningCount,
    succeeded: data.succeededCount,
    failed: data.failedCount,
  };
  tasks.forEach((task, index) => {
    const id = task?.id;
    const before = tasks[index - 1]?.id;
    if (
      typeof id !== 'string' ||
      !/^[1-9][0-9]{0,18}$/.test(id) ||
      (id.length === 19 && id > '9223372036854775807') ||
      (index > 0 &&
        !(
          id.length < before.length ||
          (id.length === before.length && id < before)
        )) ||
      !Object.prototype.hasOwnProperty.call(SOURCES, task.sourceType) ||
      !Object.prototype.hasOwnProperty.call(TASK_STATES, task.status) ||
      [task.attemptCount, task.observationCount].some(
        (v) => v != null && (!Number.isInteger(v) || v < 0 || v > 2147483647),
      ) ||
      !isTime(task.windowStartAt) ||
      !isTime(task.windowEndAt) ||
      task.windowStartAt > task.windowEndAt ||
      task.windowStartAt > data.toTime ||
      task.windowEndAt < data.fromTime ||
      [task.readyAt, task.startedAt, task.finishedAt, task.updatedAt].some(
        (v) => v != null && !isTime(v),
      )
    )
      throw new Error('Invalid cleaning task');
    const bucket = task.status === 'unknown' ? 'failed' : task.status;
    remaining[bucket] -= 1;
    if (remaining[bucket] < 0)
      throw new Error('Cleaning task details contradict counts');
  });
}
// 明确时区并展示完整日期，避免只显示时分造成歧义。
export function processingTime(value) {
  if (value == null) return '未提供';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(value);
}
export default function CommunicationProcessingPanel({ memberId }) {
  const identity = String(memberId);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const [state, setState] = useState({});
  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setState({});
    return () => {
      generation.current += 1;
      inFlight.current = false;
    };
  }, [identity]);
  const current = state.identity === identity ? state : {};
  const load = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const request = ++generation.current;
    setState({ identity, loading: true });
    try {
      const response = await getMemberCommunicationProcessing({
        requestData: { memberId },
      });
      const data = readCommunicationProcessing(response, memberId);
      if (request === generation.current) setState({ identity, data });
    } catch (error) {
      if (request === generation.current)
        setState({
          identity,
          error:
            {
              401: '请先登录后台',
              403: '无会员档案菜单权限',
            }[error?.response?.status] ||
            '沟通清洗状态暂时无法加载，请重新查询',
        });
    } finally {
      if (request === generation.current) inFlight.current = false;
    }
  };
  const data = current.data;
  return (
    <Card
      size="small"
      bordered={false}
      title="沟通证据清洗"
      className={`${styles.infoCard} ${styles.processingCard}`}
    >
      <Typography.Paragraph type="secondary" className={styles.processingIntro}>
        查看最近 24 小时企微与 1V1 清洗任务。任务成功可能提取到 0
        条事实，不等于产生了会员标签。
      </Typography.Paragraph>
      <details className={styles.processingDetails}>
        <summary>查看统计口径</summary>
        <Typography.Paragraph type="secondary">
          仅查询当前提取器版本、与最近 24
          小时重叠的清洗窗口，不是消息条数；不包含问卷标签、历史版本、课程评估或全链路健康状态。
        </Typography.Paragraph>
      </details>
      {data && (
        <>
          <Tag
            color={
              data.failedCount
                ? 'red'
                : data.pendingCount || data.runningCount
                ? 'orange'
                : 'default'
            }
          >
            {data.totalTaskCount === 0
              ? '窗口内暂无清洗任务'
              : data.failedCount
              ? '存在失败或未知状态任务'
              : data.pendingCount || data.runningCount
              ? '仍有未完成任务'
              : '窗口内清洗任务已成功'}
          </Tag>
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, lg: 3 }}
            className={styles.processingSummary}
          >
            <Descriptions.Item label="任务总数">
              {data.totalTaskCount}
            </Descriptions.Item>
            <Descriptions.Item label="等待执行">
              {data.pendingCount}
            </Descriptions.Item>
            <Descriptions.Item label="执行中">
              {data.runningCount}
            </Descriptions.Item>
            <Descriptions.Item label="成功">
              {data.succeededCount}
            </Descriptions.Item>
            <Descriptions.Item label="失败或未知">
              {data.failedCount}
            </Descriptions.Item>
            <Descriptions.Item label="查询时间（UTC+8）">
              {processingTime(data.queriedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="窗口开始（UTC+8）">
              {processingTime(data.fromTime)}
            </Descriptions.Item>
            <Descriptions.Item label="窗口结束（UTC+8）">
              {processingTime(data.toTime)}
            </Descriptions.Item>
            <Descriptions.Item label="最早就绪时间（UTC+8）">
              {processingTime(data.nextReadyAt)}
            </Descriptions.Item>
            <Descriptions.Item label="最近任务结束（UTC+8）">
              {processingTime(data.latestFinishedAt)}
            </Descriptions.Item>
          </Descriptions>
          <Typography.Paragraph type="secondary">
            就绪时间不是保证执行时间；任务结束可能是成功或失败。无任务不能证明消息已接入。查询不触发重试；仅二次确认后重新入队已耗尽自动重试的失败任务，后台处理可能调用模型。
          </Typography.Paragraph>
          {data.windowTasks == null ? (
            <Typography.Paragraph type="secondary">
              当前服务尚未提供逐条清洗任务明细，不能据此判断有无任务。
            </Typography.Paragraph>
          ) : data.windowTasks.length > 0 ? (
            <>
              <Typography.Paragraph strong>
                清洗窗口任务明细
              </Typography.Paragraph>
              <Table
                size="small"
                rowKey="id"
                dataSource={data.windowTasks}
                pagination={false}
                scroll={{ x: 1600 }}
                columns={[
                  {
                    title: '操作',
                    key: 'retry',
                    render: (_, task) => (
                      <CommunicationWindowRetryButton
                        key={`${memberId}:${task.id}:${task.updatedAt}:${task.attemptCount}`}
                        memberId={memberId}
                        task={{ ...task, taskRecordId: task.id }}
                      />
                    ),
                  },
                  { title: '任务记录 ID', dataIndex: 'id' },
                  {
                    title: '来源',
                    dataIndex: 'sourceType',
                    render: (v) => SOURCES[v],
                  },
                  {
                    title: '状态',
                    dataIndex: 'status',
                    render: (v) => TASK_STATES[v],
                  },
                  {
                    title: '尝试次数',
                    dataIndex: 'attemptCount',
                    render: (v) => v ?? '未提供',
                  },
                  {
                    title: '本任务提取事实数',
                    key: 'count',
                    render: (_, task) =>
                      task.status === 'succeeded'
                        ? task.observationCount ?? '未提供'
                        : '尚无成功结果',
                  },
                  ...[
                    ['windowStartAt', '窗口开始'],
                    ['windowEndAt', '窗口结束'],
                    ['readyAt', '就绪时间'],
                    ['startedAt', '最近启动 / 续租'],
                    ['finishedAt', '最近结束'],
                    ['updatedAt', '最近更新'],
                  ].map(([dataIndex, title]) => ({
                    title: `${title}（UTC+8）`,
                    dataIndex,
                    render: processingTime,
                  })),
                ]}
              />
              <Typography.Paragraph type="secondary">
                按任务记录 ID 倒序展示；窗口与上方查询范围重叠。成功且事实数为 0
                是有效结果，不代表错误，也不等于当前有效标签数。
              </Typography.Paragraph>
              {data.windowTasksHasMore && (
                <Typography.Paragraph type="warning">
                  仅展示最近 20
                  条，范围内还有更早任务；上方汇总仍包含全部匹配窗口。
                </Typography.Paragraph>
              )}
            </>
          ) : null}
        </>
      )}
      {current.error && (
        <Typography.Paragraph type="warning">
          {current.error}
        </Typography.Paragraph>
      )}
      <Button
        type="link"
        className={styles.processingAction}
        onClick={load}
        loading={current.loading}
        disabled={current.loading}
      >
        {current.loading
          ? '查询中…'
          : data || current.error
          ? '重新查询状态'
          : '查看处理状态'}
      </Button>
    </Card>
  );
}
CommunicationProcessingPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
